import React from 'react';
import Typography, { TypographyProps } from '../../common/Typography';

interface TermsAndConditionsProps
  extends React.HTMLAttributes<HTMLDivElement> {}

const Header: React.FC<TypographyProps> = (props) => (
  <Typography as="h4" fontWeight="bold" fontSize="16px" {...props} />
);
const P: React.FC<TypographyProps> = (props) => (
  <Typography as="p" margin="4px 0 16px 0" {...props} />
);
const Italic: React.FC<TypographyProps> = (props) => (
  <Typography as="span" fontStyle="italic" {...props} />
);
const Bold: React.FC<TypographyProps> = (props) => (
  <Typography as="span" fontWeight="500" {...props} />
);

const TermsAndConditions: React.FC<TermsAndConditionsProps> = (props) => {
  return (
    <div {...props}>
      <Header>Acknowledgement of Limitations</Header>
      <P>
        Digital/telecytology interpretations are made with the assumption that
        the images are representative of both the slides and the lesion.
        Submission of glass slides and biopsy with histologic examination is
        recommended following digital/telecytology as as indicated during
        consultation. The submitting veterinarian (“the Client”) agrees and
        acknowledges that Dr. Kate Baker/Pocket Pathologist, LLC (“the
        Consultant”) will be providing consultation services only, that the
        Client will be the only party under this Agreement to provide clinical
        services to their patients in connection with any of the services
        provided by Pocket Pathologist, LLC, and that the Client will be solely
        responsible for all patient care and treatment decisions and will be
        free to accept or reject any online second opinion consultation services
        and/or advice provided by Pocket Pathologist, LLC.
      </P>

      <Header>Digital Image Quality</Header>
      <P>
        The Client acknowledges it is their responsibility to submit quality
        images suitable for consultation. The Consultant is at liberty to
        request additional photos in order to complete the consultation and may
        decline consultation for any reason, including image quality. The Client
        acknowledges and accepts that charges will be incurred regardless of the
        diagnostic quality of the provided images and has 24 hours to submit
        additional photos at no extra charge as needed.
      </P>

      <Header>Educational/Promotional Sharing</Header>
      <P>
        Unless requested otherwise, the Client agrees to allow any submitted
        photos to be used by Dr. Kate Baker for educational or promotional
        purposes (including on social media) with the patient’s name
        removed/changed and owner information omitted for confidentiality
        purposes.{' '}
        <Italic>
          (Please indicate if you would like to be excluded from this, and your
          cases will not be shared).
        </Italic>
      </P>

      <Header>Payment Terms</Header>
      <P>
        The Client agrees to pay for all services at Pocket Pathologist&apos;s
        then-current published rates. The Client is responsible for all charges
        incurred on their account.
      </P>

      <Header>Payment Methods</Header>
      <P>Clients may choose one of the following payment options:</P>
      <P>
        1. <Bold>Payment at Time of Service (Credit Card)</Bold> — The Client
        may elect to pay by credit card at the time each case is submitted.
        Payment will be processed immediately, and no invoice will be
        generated.
      </P>
      <P>
        2. <Bold>Monthly Invoicing</Bold> — The Client may elect to have
        eligible cases billed on a monthly invoice. Invoices will be delivered
        electronically to the email address associated with the Client&apos;s
        account and are due within fourteen (14) days of the invoice delivery
        date (&quot;Net 14&quot;). Payments must be made electronically. Pocket
        Pathologist does not accept cash or paper checks.
      </P>

      <Header>Credit Card on File</Header>
      <P>
        Regardless of the selected payment method, the Client agrees to
        maintain a valid and current credit card on file at all times. The
        Client is responsible for promptly updating any expired, replaced, or
        otherwise invalid payment information.
      </P>
      <P>
        If a monthly invoice remains unpaid after the fourteen (14) day payment
        period, Pocket Pathologist will provide a seven (7) day grace period
        before processing the outstanding balance using the credit card on
        file. By maintaining a payment card on file, the Client expressly
        authorizes Pocket Pathologist to charge the card for any unpaid invoice
        balance after the expiration of the grace period.
      </P>

      <Header>Late Payments</Header>
      <P>
        Invoices not paid by the end of the seven (7) day grace period
        (twenty-one (21) days after invoice delivery) will be subject to a late
        fee equal to two percent (2%) of the outstanding invoice balance, and
        will be automatically charged to the Client&apos;s credit card on file
        for the outstanding balance, including any applicable late fees.
      </P>
      <P>
        If Pocket Pathologist is unable to process payment because the card on
        file is expired, declined, or otherwise invalid, the Client agrees to
        promptly provide an updated payment method upon request.
      </P>

      <Header>Failure to Pay</Header>
      <P>
        Pocket Pathologist reserves the right to suspend or terminate access to
        its services for any account with unpaid balances, declined payment
        methods, or repeated payment failures until all outstanding amounts
        have been paid in full. The Client remains responsible for all charges
        incurred prior to suspension or termination of services.
      </P>

      <Header>Termination of Services</Header>
      <P>
        Both the Client and Consultant are at liberty to terminate services for
        any reason at any time, but the Client will be responsible for any
        charges incurred during the term of service.
      </P>
    </div>
  );
};

export default TermsAndConditions;
